import axios from 'axios';
import { merge } from 'lodash';
import * as clientCache from '../clientCache';
import { count } from '../monitor';
import {
    DEFAULT_SOURCE_INFO,
    DEFAULT_TIMEOUT_POLICY,
    DEFAULT_AB_TESTS
} from './constants';
import { createAxiosInstance, retryInterceptor } from './axios';

/**
 * An api for translation, currently supports a single string
 * @param {string} options.url - The url to Machine Translation service
 * @param {string} options.text - The string to translate
 * @param {string} options.targetLocale - The target language code
 * @param {Boolean} options.useClientCache - Apply client-side cache or not
 * @param {Object} options.sourceInfo - The source information from which the method is called
 * @param {Boolean} options.retry - Retry a failed response or not
 * @param {String} options.timeoutPolicy - Set desired timeout policy
 * @returns {Promise} Returns a promise with the translation
 */
export const clientTranslate = (options) => {
    const {
        url,
        text,
        targetLocale,
        useClientCache = false,
        sourceInfo = DEFAULT_SOURCE_INFO,
        retry = false,
        timeoutPolicy = DEFAULT_TIMEOUT_POLICY
    } = options;

    const formattedTargetLocale = formatLocale(targetLocale);

    if (useClientCache) {
        const cacheData = clientCache.get({ text, targetLocale: formattedTargetLocale });
        if (cacheData) {
            monitorCache(sourceInfo.name, 'hit');
            return Promise.resolve(cacheData);
        } else {
            monitorCache(sourceInfo.name, 'miss');
        }
    }

    const postParams = merge(options, {
        text,
        targetLocale: formattedTargetLocale,
        sourceInfo,
        timeoutPolicy
    });

    const axiosInstance = createAxiosInstance();
    retry && retryInterceptor(axiosInstance);

    return axiosInstance.post(url, postParams)
        .then(({ data, status }) => {
            if (useClientCache && status === 200) {
                clientCache.set({ text, targetLocale, data });
            }

            return { ...data, status };
        });
};

/**
 * An api for translation, currently supports a single string
 * @param {string} url - The url to Machine Translation service
 * @param {string} pegasusUrl - The url to the Machine Translation Pegasus service
 * @param {string} text - The string to translate
 * @param {string} targetLocale - The target language code
 * @param {string} sourceLocale - The source language code
 * @param {Boolean} useServerCache - Apply server-side cache or not
 * @param {Boolean} writeCache - Write translation to server-side cache
 * @param {Object} sourceInfo - The source information from which the method is called
 * @param {Object} context - The translation context
 * @param {Boolean} knownCrawler - Did a known crawler made the request.
 * @param {Number} timeout - Set desired timeout
 * @param {Object} abTests - Which tests the user is part of
 * @returns {Promise} Returns a promise with the translation
 */
export const serverTranslate = async({
    pegasusUrl,
    text,
    targetLocale,
    sourceLocale,
    useServerCache = false,
    writeCache = true,
    sourceInfo = DEFAULT_SOURCE_INFO,
    context,
    knownCrawler = false,
    timeout,
    abTests = DEFAULT_AB_TESTS
}) => {
    const params = convertPropsToPegasusProps({
        text,
        targetLocale,
        sourceLocale,
        useServerCache,
        writeCache,
        abTests,
        timeout,
        context,
        knownCrawler,
        sourceInfo
    });

    let response;
    try {
        response = await axios.post(pegasusUrl, params)
            .then(({ data, status }) => ({ status, ...data }));

        monitorStatus({
            source: sourceInfo?.name,
            status: response?.status
        });
    } catch (e) {
        monitorStatus({
            source: sourceInfo?.name,
            status: 'ERROR'
        });

        throw e;
    }

    return formatResponse(response);
};

export const formatLocale = (locale) => {
    if (!locale || locale === 'auto') {
        return locale;
    }
    return locale.substr(0, 2).toLowerCase();
};

const monitorCache = (source, key) => {
    count(`source.${source}.clientCache.${key}`);
};

const monitorStatus = ({ source, status, usePegasus }) => {
    count(`source.${source}.status.${status}.variant.${usePegasus ? 'mtpegasus' : 'mtservice'}`);
};

const convertPropsToPegasusProps = (props) => ({
    term: props.text,
    targetLanguage: formatLocale(props.targetLocale),
    sourceLanguage: formatLocale(props.sourceLocale),
    readCache: props.useServerCache,
    writeCache: props.writeCache,
    abTests: props.abTests,
    timeout: props.timeout,
    context: props.context || props.sourceInfo.context,
    knownCrawler: props.knownCrawler,
    sourceInfo: props.sourceInfo
});

const formatResponse = (response) => ({
    originalBlob: response.originalTerm,
    translatedBlob: response.translatedTerm,
    sourceLocale: response.sourceLocale,
    status: convertStatusNameToHttpCode(response.status)
});

export const convertStatusNameToHttpCode = (status) => {
    const statusesMap = {
        Success: 200,
        Failure: 500,
        Partial: 207
    };

    return statusesMap[status] || status;
};
